import { gpx } from '@mapbox/togeojson'
import { simplify } from '@turf/simplify'
import { isNonEmptyArray } from 'effect/Array'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader } from 'components/common/Loader'
import { NewFlexContainer } from 'components/common/common.style'
import { GpxDownloadButton } from 'components/event/GpxDownloadButton'
import { LocationContent } from 'containers/event/map/MapContainer.style'
import { calculateMapBounds } from 'containers/seoPages/map/SearchStaticMap'
import { unifiedColors } from 'styles/colors'
import Map from './Map'
import { StaticMap } from './StaticMap'

interface Props {
  gpxUrl: string
}

interface GpxMapProps {
  latitude: number
  longitude: number
  geoJson: GeoJSON.Feature<GeoJSON.LineString>
  geoJsonCollection: GeoJSON.FeatureCollection
}

const getSimplifiedGeoJson = (
  geoJson: GeoJSON.Feature<GeoJSON.LineString>,
  tolerance = 0.0005
): GeoJSON.Feature<GeoJSON.LineString> => {
  const simplified = simplify(geoJson, {
    tolerance,
    highQuality: false
  })
  if (simplified.geometry.coordinates.length > 210) {
    return getSimplifiedGeoJson(simplified, tolerance + 0.00005)
  }
  return {
    ...simplified,
    geometry: {
      ...simplified.geometry,
      coordinates: simplified.geometry.coordinates.map((coordinate) =>
        coordinate.map((position) => parseFloat(position.toFixed(6)))
      )
    }
  }
}

export const GpxMap = ({ gpxUrl }: Props) => {
  const [showDynamicMap, setShowDynamicMap] = useState(false)
  const [showStaticMap, setShowStaticMap] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [geoJsonFeature, setGeoJsonFeature] = useState<GpxMapProps | null>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setShowDynamicMap(false)
    setShowStaticMap(true)
    setIsLoading(true)
    const fetchGeoJson = async () => {
      const response = await fetch(gpxUrl)
      const gpxText = await response.text()
      const gpxDom = new DOMParser().parseFromString(gpxText, 'application/xml')
      const geojson = gpx(gpxDom)
      const firstFeature = geojson
        .features[0] as GeoJSON.Feature<GeoJSON.LineString>
      firstFeature &&
        isNonEmptyArray(firstFeature.geometry.coordinates) &&
        setGeoJsonFeature({
          latitude: firstFeature.geometry.coordinates[0][1] ?? 0,
          longitude: firstFeature.geometry.coordinates[0][0] ?? 0,

          geoJson: {
            ...firstFeature,
            properties: {
              stroke: unifiedColors.button.primary.default,
              'stroke-width': 3,
              coordTimes: []
            }
          },
          geoJsonCollection: geojson
        })
      setIsLoading(false)
    }
    fetchGeoJson()
  }, [gpxUrl])
  const simplifiedGeoJson = useMemo(
    () =>
      geoJsonFeature?.geoJson
        ? getSimplifiedGeoJson(geoJsonFeature.geoJson)
        : null,
    [geoJsonFeature]
  )
  const bounds = useMemo(() => {
    if (simplifiedGeoJson) {
      return calculateMapBounds(
        simplifiedGeoJson.geometry.coordinates.map((coordinates) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates },
          properties: {}
        }))
      )
    }
    return null
  }, [simplifiedGeoJson])
  return (
    <LocationContent ref={mapRef}>
      {isLoading ? (
        <NewFlexContainer
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <Loader />
        </NewFlexContainer>
      ) : (
        geoJsonFeature &&
        simplifiedGeoJson && (
          <>
            <GpxDownloadButton gpxUrl={gpxUrl} />
            {showDynamicMap && (
              <Map
                latitude={geoJsonFeature.latitude}
                longitude={geoJsonFeature.longitude}
                onLoad={() => setShowStaticMap(false)}
                geoJson={geoJsonFeature.geoJsonCollection}
                bounds={bounds}
              />
            )}
            {showStaticMap && mapRef.current && (
              <StaticMap
                latitude={geoJsonFeature.latitude}
                longitude={geoJsonFeature.longitude}
                geoJson={{
                  geoJson: simplifiedGeoJson
                }}
                bounds={bounds}
                mapRef={mapRef}
                pointerEventsNone={showDynamicMap}
                onMouseDown={() => setShowDynamicMap(true)}
              />
            )}
          </>
        )
      )}
    </LocationContent>
  )
}
